import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {createProjectTime, deleteProjectTime, listProjectTimes, updateProjectTime} from '../api/monthlyRecordingApi';
import {EditMonthlyProjectTime} from '../components/EditMonthlyProjectTime';
import {type MonthlyEntryFormValues, MonthlyProjectTimeForm} from '../components/MonthlyProjectTimeForm';
import {MonthlyProjectTimeTable} from '../components/MonthlyProjectTimeTable';
import {TaskTotalsPanel} from '../components/TaskTotalsPanel';
import {MonthYearSelector} from '../components/MonthYearSelector';
import {formatMessage, getMessage} from '../utils/messages';
import type {Config, ProjectTimeRecord, TaskOption} from '../types/types';
import * as BootstrapModal from "@/shared/ui/bootstrapModal";
import {formatDateTime, formatDuration, getISOZonedDateTime, parseISOZonedDateTime, Temporal} from "@/shared/utils/datefunctions";

const isStartOfMonthMidnight = (date: Temporal.ZonedDateTime) => date.equals(date.with({
	day: 1,
	hour: 0,
	minute: 0,
	second: 0,
	millisecond: 0
}));
const SESSION_STORAGE_MONTH_YEAR = 'monthlyRecording.monthYear.v2';

const loadMonthYearFromSession = (): Temporal.PlainYearMonth | null => {
	try {
		const rawValue = sessionStorage.getItem(SESSION_STORAGE_MONTH_YEAR);
		if (!rawValue) {
			return null;
		}
		return Temporal.PlainYearMonth.from(rawValue)
	} catch {
		return null;
	}
};

const buildDeleteDialogMessage = (entry: ProjectTimeRecord) => {
	const descriptionMessage = entry.description ? formatMessage('record.delete.message.description', entry.description) : '';
	const descriptionPart = descriptionMessage ? ` ${descriptionMessage}` : '';
	let startText = '';
	if (entry.start) {
		const parsedDate = parseISOZonedDateTime(entry.start);
		startText = parsedDate ? formatDateTime(parsedDate) : '';
	}
	return formatMessage(
		'projectTime.delete.message',
		entry.task?.name ?? getMessage('monthlyRecording.delete.unnamedRecord'),
		descriptionPart,
		startText,
	);
};

const confirmEntryDeletion = (entry: ProjectTimeRecord): Promise<boolean> => {
	if (!entry.id) {
		return Promise.resolve(true);
	}
	return new Promise(resolve => {
		let resolved = false;
		const resolveOnce = (result: boolean) => {
			if (resolved) {
				return;
			}
			resolved = true;
			resolve(result);
		};
		BootstrapModal.confirm({
			title: 'projectTime.delete.title',
			message: buildDeleteDialogMessage(entry),
			yesLabel: 'confirmation.delete',
			noLabel: 'confirmation.cancel',
			yesAction: modal => {
				modal.close('yes');
				resolveOnce(true);
			},
			noAction: modal => {
				modal.close('no');
				resolveOnce(false);
			},
			onClose: (_modal, reason) => {
				if (reason === BootstrapModal.CLOSE_REASON_INTERNAL) {
					resolveOnce(false);
				}
			},
		});
	});
};

export const MonthlyRecordingApp = ({config}: { config: Config }) => {
	const [entries, setEntries] = useState<ProjectTimeRecord[]>([]);
	const [isLoading, setIsLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const initialMonthYear = useMemo(() => loadMonthYearFromSession(), []);
	const [yearMonth, setYearMonth] = useState<Temporal.PlainYearMonth>(initialMonthYear ?? Temporal.Now.plainDateISO().toPlainYearMonth())
	const [editingRecord, setEditingRecord] = useState<ProjectTimeRecord | null>(null);
	const [isSaving, setIsSaving] = useState(false);

	const monthDurationMs = useMemo(() => {
		const tz = Temporal.Now.timeZoneId();
		const monthStart = yearMonth.toPlainDate({day: 1}).toZonedDateTime(tz);
		const nextMonthStart = monthStart.add({months: 1});
		return monthStart.until(nextMonthStart, {largestUnit: 'hour'}).total('milliseconds');
	}, [yearMonth]);

	const sortEntries = (records: ProjectTimeRecord[]) =>
		[...records].sort((a, b) => {
			const aStart = parseISOZonedDateTime(a.start);
			const bStart = parseISOZonedDateTime(b.start);
			if (aStart && bStart) {
				const cmp = Temporal.ZonedDateTime.compare(aStart, bStart);
				if (cmp !== 0) return cmp;
			}
			return b.id - a.id;
		});

	const loadEntries = useCallback(async () => {
		setIsLoading(true);
		try {
			const data = await listProjectTimes(config, yearMonth);
			setEntries(sortEntries(data));
			setError(null);
		} catch (e) {
			setError((e as Error).message);
		} finally {
			setIsLoading(false);
		}
	}, [config.baseUrl, config.csrf, yearMonth]);

	useEffect(() => {
		void loadEntries();
	}, [loadEntries]);

	useEffect(() => {
		resetForm();
	}, [yearMonth]);

	useEffect(() => {
		sessionStorage.setItem(SESSION_STORAGE_MONTH_YEAR, yearMonth.toString());
	}, [yearMonth]);

	const resetForm = () => {
		setEditingRecord(null);
	};

	const startEdit = (entry?: ProjectTimeRecord) => {
		setEditingRecord(entry ?? null);
	};

	const totalDurations = useMemo(() => {
		const totals = new Map<number, { task: TaskOption; duration: number }>();
		let overall = 0;
		entries.forEach(entry => {
			const taskId = entry.task?.id;
			if (!taskId || !entry.durationNet || !entry.task) {
				return;
			}
			const current = totals.get(taskId);
			const sum = (current?.duration ?? 0) + entry.durationNet;
			totals.set(taskId, {task: entry.task, duration: sum});
			overall += entry.durationNet;
		});
		const sorted = Array.from(totals.values()).sort((a, b) => b.duration - a.duration);
		return {sorted, overall};
	}, [entries]);

	const editingRecordStart = useMemo(
		() => (editingRecord?.start ? parseISOZonedDateTime(editingRecord.start) : null),
		[editingRecord],
	);
	const canEditDuration = editingRecord === null || (editingRecordStart ? isStartOfMonthMidnight(editingRecordStart) : false);

	const saveEntry = async ({task, comment, durationMs}: MonthlyEntryFormValues) => {
		const isNewEntry = editingRecord === null;
		const payload: ProjectTimeRecord = {
			...editingRecord,
			task: {id: task.id, name: task.name},
			description: comment,
		};
		if (isNewEntry || canEditDuration) {
			const tz = Temporal.Now.timeZoneId();
			const start = yearMonth.toPlainDate({day: 1}).toZonedDateTime(tz);
			const end = start.add({milliseconds: durationMs});
			const startOfNextMonth = start.add({months: 1});
			if (Temporal.ZonedDateTime.compare(end, startOfNextMonth) > 0) {
				throw new Error(getMessage('monthlyRecording.error.exceedsMonth'));
			}
			payload.start = getISOZonedDateTime(start);
			payload.end = getISOZonedDateTime(end);
			payload.durationGross = durationMs;
			payload.durationNet = durationMs;
		}

		setIsSaving(true);
		try {
			if (isNewEntry) {
				await createProjectTime(config, payload);
			} else if (payload.id) {
				await updateProjectTime(config, payload);
			}
			await loadEntries();
			resetForm();
		} catch (e) {
			setError((e as Error).message);
		} finally {
			setIsSaving(false);
		}
	};

	const deleteEntry = async (entry: ProjectTimeRecord) => {
		if (!entry.id) {
			return;
		}
		setIsSaving(true);
		try {
			await deleteProjectTime(config, entry.id);
			await loadEntries();
			resetForm();
		} catch (e) {
			setError((e as Error).message);
		} finally {
			setIsSaving(false);
		}
	};

	const requestDeleteEntry = async (entry: ProjectTimeRecord) => {
		const shouldDelete = await confirmEntryDeletion(entry);
		if (!shouldDelete) {
			return;
		}
		await deleteEntry(entry);
	};

	const monthNames: string[] = (globals?.monthNames ?? 'Jänner,Februar,März,April,Mai,Juni,Juli,August,September,Oktober,November,Dezember'.split(','))
		.map(name => name.trim());

	const editableStatus = (entry: ProjectTimeRecord) => entry.status === 'CHANGEABLE';
	const totalDurationAll = totalDurations.overall;
	const formId = 'monthly-project-time-add-form';

	return (
		<div>
			{error && <div className="alert alert-danger">{error}</div>}
			<div className="row mb-20">
				<div className="col-sm-12 col-md-8 col-lg-9">
					<MonthYearSelector
						month={yearMonth.month}
						year={yearMonth.year}
						monthNames={monthNames}
						onMonthChange={month => setYearMonth(yearMonth.with({month}))}
						onYearChange={year => setYearMonth(yearMonth.with({year}))}
					/>
				</div>
			</div>

			<div className="row mb-20">
				<div className="col-lg-7 col-md-7">
					<div className="panel panel-recording">
						<div className="panel-body">
							<MonthlyProjectTimeForm
								config={config}
								maxDurationMs={monthDurationMs}
								canEditDuration={true}
								disabled={isSaving}
								onSubmit={saveEntry}
								formId={formId}
							/>
							<div className="form-group">
								<div className="col-xs-12 text-right">
									<button
										type="submit"
										form={formId}
										className="btn btn-primary"
										disabled={isSaving}
									>
										{getMessage('dialog.save')}
									</button>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>

			<div className="row mb-20">
				<div className="col-sm-12 col-md-4 col-lg-3 text-left">
					<div className="text-muted">{getMessage('report.total')}</div>
					<h3 className="mt-5 mb-0">
						{totalDurationAll ? formatDuration(totalDurationAll) : '00:00'}
					</h3>
				</div>
			</div>

			<div className="row">
				<div className="col-lg-8 col-md-8">
					<MonthlyProjectTimeTable
						entries={entries}
						isLoading={isLoading}
						isSaving={isSaving}
						isFormOpen={editingRecord !== null}
						canEditEntry={editableStatus}
						onStartEdit={startEdit}
						onDeleteEntry={requestDeleteEntry}
					/>
				</div>
				<div className="col-lg-4 col-md-4">
					<TaskTotalsPanel
						totalDurations={totalDurations}
					/>
				</div>
			</div>
			<EditMonthlyProjectTime
				config={config}
				maxDurationMs={monthDurationMs}
				record={editingRecord ?? undefined}
				canEditDuration={canEditDuration}
				disabled={isSaving}
				isOpen={editingRecord !== null}
				onSubmit={saveEntry}
				onCancel={resetForm}
				onDelete={editingRecord ? () => requestDeleteEntry(editingRecord) : undefined}
			/>
		</div>
	);
};
